import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { refreshCoin } from '../../redux/slices/coinsSlice';
import WalletDashboardCard from '../../components/WalletDashboardCard';
import { useTransactionHistory } from '../../hooks/useTransactionHistory';
import { ActionButtons, CoinHeader, CoinStats, PriceChart, ProtocolInfo } from './components';
import { CoinDetailsSkeleton } from '../../components/skeletons';
import TransactionHistory from '../../components/shared/TransactionHistory';
import { useTour } from '../../hooks/useTour';

import SendModal from '../../components/SendModal';
import ReceiveModal from '../../components/ReceiveModal';
import PrivateTransferModal from '../../components/InternalTransferModal';
import { ShieldModal } from '../../components/shield';
import { showToast } from '../../redux/slices/toastSlice';

const CoinDetails = () => {
  const { coinId } = useParams();
  const dispatch = useDispatch();
  const mode = useSelector(state => state.mode.mode);

  // Local state for coin data from refreshCoin API response
  const [coin, setCoin] = useState(null);
  const [loading, setLoading] = useState(true);
  const { startTour } = useTour();


  const {
    transactions,
    loading: transactionLoading,
    pagination,
    address: apiAddress,
    fetchTransactionHistory
  } = useTransactionHistory(coinId);

  // Modal states
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [privateTransferModalOpen, setPrivateTransferModalOpen] = useState(false);
  const [shieldUnshieldModalOpen, setShieldUnshieldModalOpen] = useState(false);

  // Fetch coin data using refreshCoin action
  const fetchCoinData = useCallback(async () => {
    if (!coinId || !mode) return;

    setLoading(true);
    try {
      const result = await dispatch(refreshCoin({ coinId, mode }));

      if (result.payload && result.payload.coinData) {
        setCoin(result.payload.coinData);
        // Also refresh transaction history
        fetchTransactionHistory();
      }
    } catch (error) {
      console.error('Error fetching coin data:', error);
    } finally {
      setLoading(false);
    }
  }, [coinId, mode, dispatch, fetchTransactionHistory]);

  useEffect(() => {
    if (coin)
      document.title = coin?.name || "Coin Details"
  }, [coin])

  // Call refreshCoin when component mounts
  useEffect(() => {
    fetchCoinData();
  }, [fetchCoinData]);

  useEffect(() => {
    if (!loading && coin) {
      const timer = setTimeout(() => {
        startTour(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, coin, startTour]);


  // Loading state
  if (loading) {
    return <CoinDetailsSkeleton />
  }

  // Error state
  if (!coin) {
    return <div className="p-5">Coin not found</div>;
  }

  const handleShieldUnshieldClick = () => {
    if (!coin.isShieldable) {
      dispatch(showToast({ msg: "This coin is not shieldable", state: "warning" }));
      return;
    }
    setShieldUnshieldModalOpen(true);
  };

  return (
    <div className="">
      <main className="wallet-dashboard main-page">
        <div className="coin-details-page">
          <div className="wallet-dashboard p-0">
            <div className="grid lg:grid-cols-[450px_1fr] grid-cols-1 gap-[20px] mb-3">
              <div className="flex flex-col">
                <WalletDashboardCard />
                <div className="hidden lg:block">
                  <ProtocolInfo coin={coin.name} />
                </div>
              </div>

              <div id="tour-coin-details" className="flex flex-col">
                <CoinHeader
                  token={coin}
                  coin={{ ...coin, address: apiAddress || coin.address }}
                  mode={mode}
                  onRefresh={fetchCoinData}
                />
                <CoinStats coin={coin} />
                <PriceChart coin={coin} />
                <ActionButtons
                  coin={coin}
                  mode={mode}
                  onSendClick={() => setSendModalOpen(true)}
                  onReceiveClick={() => setReceiveModalOpen(true)}
                  onPrivateTransferClick={() => setPrivateTransferModalOpen(true)}
                  onShieldUnshieldClick={handleShieldUnshieldClick}
                />
              </div>
            </div>
            <div className="mt-4">
              <TransactionHistory
                transactions={transactions}
                loading={transactionLoading}
                pagination={pagination}
                onPageChange={fetchTransactionHistory}
              />
            </div>
          </div>

          {/* Modals */}
          {sendModalOpen && <SendModal onClose={() => setSendModalOpen(false)} selectedToken={coin} />}
          {receiveModalOpen && <ReceiveModal selectedToken={coin} onClose={() => setReceiveModalOpen(false)} />}
          {privateTransferModalOpen && <PrivateTransferModal onClose={() => setPrivateTransferModalOpen(false)} selectedToken={coin} />}
          {shieldUnshieldModalOpen && (
            <ShieldModal
              onClose={() => setShieldUnshieldModalOpen(false)}
              selectedToken={coin}
              mode={mode}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default CoinDetails;